import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  Select,
  MenuItem,
} from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useAuth } from "../context/AuthContext";
import { markAs } from "../api/dashboard";

const roomStatusColors = {
  available: "#E0F7FA",
  occupied: "#FFEBEE",
  cleaning: "#FFF8E1",
};

const RoomStatusCard = ({ room, status }) => {
  const statusColor = roomStatusColors[status] || "#ECEFF1";
  const statusIcon = {
    available: <HotelIcon color="success" />,
    occupied: <CheckCircleIcon color="error" />,
    cleaning: <CancelIcon color="warning" />,
  }[status];

  return (
    <Tooltip title={status.toUpperCase()} arrow>
      <Box
        sx={{
          flex: "1 1 130px",
          maxWidth: "160px",
          backgroundColor: statusColor,
          borderRadius: 3,
          padding: 2,
          margin: 1,
          textAlign: "center",
          boxShadow: 2,
          transition: "transform 0.2s",
          "&:hover": {
            transform: "scale(1.03)",
          },
        }}
      >
        <Box mb={1}>{statusIcon}</Box>
        <Typography variant="subtitle2" fontWeight="bold">
          {room.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {room.type}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const RoomOccupancyDashboard = ({
  activeReservations = [],
  refetchAlojamiento,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [nuevoEstado, setNuevoEstado] = useState("true");
  const [selectedEmpleado, setSelectedEmpleado] = useState("");
  const { user } = useAuth();

  const rooms = user?.rooms || [];
  const employes = user?.employes || [];

  const getRoomStatus = (room) => {
    return activeReservations.some((res) => {
      const estado = (res.estadoIn + "").toLowerCase() === "true";
      if (!estado) return false;
      if (room.type === "private") {
        return (
          Number(res.unitId) === Number(room.unitId) &&
          Number(res.roomId) === Number(room.roomId)
        );
      }
      if (room.type === "shared") {
        return Number(res.roomId) === Number(room.roomId);
      }
      return false;
    })
      ? "occupied"
      : "available";
  };

  const onRoomAction = (reserva) => {
    const estadoActual = (reserva.estadoIn + "").toLowerCase();
    const estadoFinal = estadoActual === "true" ? "out" : "true";
    setSelectedReserva(reserva);
    setNuevoEstado(estadoFinal);
    setModalOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedReserva || !selectedEmpleado) return;
    const body = {
      estadoIn: nuevoEstado,
      empleado: selectedEmpleado,
      idbeds: selectedReserva.idbeds,
    };
    try {
      await markAs(body);
      await refetchAlojamiento();
      setModalOpen(false);
      setSelectedReserva(null);
      setSelectedEmpleado("");
    } catch (error) {
      console.error("❌ Error al marcar ingreso/salida:", error);
      setModalOpen(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
    const targetDate = new Date(year, month - 1, day);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const clearTime = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const target = clearTime(targetDate);
    const now = clearTime(today);
    const next = clearTime(tomorrow);
    if (target.getTime() === now.getTime()) return "HOY";
    if (target.getTime() === next.getTime()) return "MAÑANA";
    return `${String(day).padStart(2, "0")}/${String(month).padStart(
      2,
      "0"
    )}/${year}`;
  };

  return (
    <Box sx={{ padding: 4 }}>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            width: 360,
            maxWidth: "90%",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            gutterBottom
          >
            Confirmar acción
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            mb={2}
          >
            Estás marcando {nuevoEstado === "true" ? "INGRESO" : "SALIDA"} para:
          </Typography>
          <Typography variant="h6" textAlign="center" gutterBottom>
            {selectedReserva?.fullName}
          </Typography>
          <Select
            fullWidth
            size="small"
            value={selectedEmpleado}
            onChange={(e) => setSelectedEmpleado(e.target.value)}
            sx={{ mb: 3 }}
          >
            {employes.length > 0 ? (
              employes.map((emp, idx) => (
                <MenuItem key={idx} value={emp}>
                  {emp}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No hay empleados registrados</MenuItem>
            )}
          </Select>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setModalOpen(false)}
              >
                Cancelar
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleConfirmAction}
                disabled={!selectedEmpleado}
              >
                Confirmar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <Box mb={4}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Estado de Habitaciones
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "start",
            alignItems: "stretch",
          }}
        >
          {rooms.map((room) => (
            <RoomStatusCard
              key={room.id}
              room={room}
              status={getRoomStatus(room)}
            />
          ))}
        </Box>
      </Box>

      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Detalles de Reservas Activas
      </Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#ede7f6" }}>
            <TableRow>
              <TableCell>Estado</TableCell>
              <TableCell>Pendiente</TableCell>
              <TableCell>Habitación</TableCell>
              <TableCell>Huéspedes</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Retiro</TableCell>
              <TableCell>Noches</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeReservations.length > 0 ? (
              Object.values(
                activeReservations.reduce((acc, res) => {
                  const id = res.reservationId;
                  if (!acc[id])
                    acc[id] = {
                      ...res,
                      all: [res],
                      hasIn: false,
                      hasOut: false,
                    };
                  else acc[id].all.push(res);
                  const estado = (res.estadoIn || "").toLowerCase();
                  if (estado === "true") acc[id].hasIn = true;
                  if (estado === "out") acc[id].hasOut = true;
                  return acc;
                }, {})
              )
                .filter((grouped) => !grouped.hasOut)
                .map((res, idx) => {
                  const seña = res.seña || 0;
                  const total = res.amount || 0;
                  const diferencia = total - seña;
                  const roomName = (() => {
                    const match = rooms.find(
                      (r) =>
                        String(r.roomId) === String(res.roomId) &&
                        String(r.unitId) === String(res.unitId)
                    );

                    if (match) return match.name;

                    const shared = rooms.find(
                      (r) =>
                        String(r.roomId) === String(res.roomId) &&
                        r.type === "shared"
                    );

                    return shared?.name || `[roomId: ${res.roomId}]`;
                  })();

                  const noches = Math.max(
                    1,
                    Math.ceil(
                      (new Date(res.endDate) - new Date(res.startDate)) /
                        (1000 * 60 * 60 * 24)
                    )
                  );
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        {res.hasIn ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => onRoomAction(res)}
                          >
                            Salida
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onRoomAction(res)}
                          >
                            Ingreso
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>${diferencia.toLocaleString()}</TableCell>
                      <TableCell>{roomName}</TableCell>
                      <TableCell>
                        {res.guests || res.numGuests || "-"}
                      </TableCell>
                      <TableCell>{res.fullName}</TableCell>
                      <TableCell>{formatDate(res.endDate)}</TableCell>
                      <TableCell>{noches}</TableCell>
                    </TableRow>
                  );
                })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No hay reservas activas.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RoomOccupancyDashboard;
